import { Container } from '../../../../types/canvas.types';

/**
 * Get display name for a container item
 * @param item - Container item (field, widget, category, connector)
 * @returns Display name for the item
 */
export const getItemDisplayName = (item: any): string => {
  if (item.type === 'field') {
    return item.name || item.configuration?.fieldName || 'Unnamed Field';
  }

  if (item.type === 'widget') {
    const widgetType = item.widgetType || 'Unknown Widget';
    return widgetType.charAt(0).toUpperCase() + widgetType.slice(1).replace(/([A-Z])/g, ' $1').trim();
  }

  if (item.type === 'category' || item.fieldType === 'category') {
    return item.name || 'Category';
  }

  if (item.type === 'connector' || item.fieldType === 'connector') {
    return item.name || 'Connector';
  }

  return item.name || 'Unnamed Item';
};

/**
 * Get type label for a container item
 * @param item - Container item
 * @returns Type label in parentheses
 */
export const getItemTypeLabel = (item: any): string => {
  if (item.type === 'field') {
    return `(${item.fieldType || 'field'})`;
  }
  if (item.type === 'widget') {
    return '(widget)';
  }
  if (item.type === 'category' || item.fieldType === 'category') {
    return '(category)';
  }
  if (item.type === 'connector' || item.fieldType === 'connector') {
    return '(connector)';
  }
  return '';
};

/**
 * Truncate a name to a maximum length
 * @param name - Name to truncate
 * @param maxLength - Maximum length (default: 25)
 * @returns Truncated name with ellipsis if needed
 */
export const truncateName = (name: string, maxLength: number = 25): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
};

/**
 * Check if a container is nested
 * @param containerId - ID of the container to check
 * @param activePageId - Active page ID
 * @param containers - All containers by page
 * @returns True if container is nested, false otherwise
 */
export const isContainerNested = (
  containerId: string,
  activePageId: string | null,
  containers: Record<string, Container[]>
): boolean => {
  if (!containerId || !activePageId || !containers[activePageId]) {
    return false;
  }

  const findContainer = (containerList: Container[]): Container | null => {
    for (const container of containerList) {
      if (container.id === containerId) {
        return container;
      }
      if (
        container &&
        container?.content &&
        container?.content?.nestedContainers &&
        container?.content?.nestedContainers?.length > 0
      ) {
        const found = findContainer(container.content.nestedContainers);
        if (found) return found;
      }
    }
    return null;
  };

  const container = findContainer(containers[activePageId]);
  return container?.metadata?.isNested === true;
};

/**
 * Convert container data to configuration format
 * @param container - Container to convert
 * @returns Configuration type object
 */
export const convertContainerToConfig = (container: Container) => {
  const widthMatch = container.configuration.width?.match(/^(\d+)(%|px)$/);
  const width = widthMatch ? parseInt(widthMatch[1]) : 100;
  const widthUnit = widthMatch && widthMatch[2] === 'px' ? 'px' : '%';

  let height: number | 'Dynamic' = 'Dynamic';
  let heightUnit: 'px' = 'px';

  if (container.configuration.height !== 'auto' && container.configuration.height !== 'Dynamic') {
    const heightMatch = String(container.configuration.height).match(/^(\d+)(px)$/);
    if (heightMatch) {
      height = parseInt(heightMatch[1]);
    }
  }

  return {
    id: container.id,
    name: container.name,
    width,
    widthUnit: widthUnit as '%' | 'px',
    height,
    heightUnit,
    alignment: container.configuration.alignment as 'left' | 'center' | 'right',
    layout: container.configuration.layout as 'vertical' | 'horizontal',
    showTitle: container.configuration.showTitle,
    widthLocked: container.configuration.widthLocked || false,
    collapsible: container.configuration.collapsible || false,
    behaveAsFilter: container.configuration.behaveAsFilter || false
  };
};

/**
 * Find containers in the same nested row as the target
 * @param containers - Array of containers to search
 * @param nestedContainerId - ID of the target nested container
 * @returns Array of containers in the same row
 */
export const findContainersInSameNestedRow = (
  containers: Container[],
  nestedContainerId: string
): Container[] => {
  for (const container of containers) {
    if (!container.content.nestedContainers) continue;

    const nestedContainer = container.content.nestedContainers.find(
      nc => nc.id === nestedContainerId
    );

    if (nestedContainer) {
      const targetRowIndex = nestedContainer.metadata?.rowIndex ?? 0;

      return container.content.nestedContainers.filter(nc => {
        const ncRowIndex = nc.metadata?.rowIndex ?? 0;
        return ncRowIndex === targetRowIndex;
      });
    }

    for (const nestedContainer of container.content.nestedContainers) {
      if (nestedContainer.content.nestedContainers && nestedContainer.content.nestedContainers.length > 0) {
        const result = findContainersInSameNestedRow(
          [nestedContainer],
          nestedContainerId
        );
        if (result.length > 0) return result;
      }
    }
  }

  return [];
};
